import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Handles the OAuth redirect from Emergent Google Auth.
 * The session_id arrives in the URL fragment: /dashboard#session_id=xxx
 * This component exchanges it for a FiPr.ai JWT and navigates to dashboard.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      toast.error('Google login failed — no session ID received');
      navigate('/auth');
      return;
    }

    // Per-session guard using sessionStorage — survives React StrictMode remounts
    // and tab refresh. Prevents re-consumption of an already-exchanged session_id.
    const guardKey = `google_session_used:${sessionId}`;
    if (sessionStorage.getItem(guardKey)) {
      // Already exchanged this session_id in this tab — go home.
      navigate('/dashboard', { replace: true });
      return;
    }
    sessionStorage.setItem(guardKey, '1');

    // Clear the #session_id=… hash from the URL IMMEDIATELY so refresh /
    // back-button never re-triggers the exchange with a consumed session_id.
    if (window.history && window.history.replaceState) {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    }

    const exchange = async () => {
      try {
        const { data } = await axios.post(`${API}/auth/google`, { session_id: sessionId });
        const { access_token, user } = data;

        // Update AuthContext state BEFORE navigating so ProtectedRoute
        // sees isAuthenticated=true immediately on the next render.
        loginWithToken(access_token, user);

        navigate('/dashboard', { replace: true });
      } catch (err) {
        const status = err.response?.status;
        const rawDetail = err.response?.data?.detail;
        const detail = typeof rawDetail === 'string' ? rawDetail : (err.message || 'Unknown error');
        console.error('Google auth callback error:', err);
        console.error('Backend error detail:', detail);

        // Detect "session expired / not found" from the upstream Emergent Auth
        // 404 message that our backend wraps into its 401 response.
        const isSessionExpired =
          status === 401 &&
          typeof detail === 'string' &&
          (detail.includes('user_data_not_found') ||
            detail.includes('404') ||
            detail.toLowerCase().includes('expired'));

        if (isSessionExpired) {
          toast.error('Your Google session expired. Please try signing in again.', { duration: 8000 });
        } else {
          toast.error(`Google login failed: ${detail}`, { duration: 8000 });
        }
        navigate('/auth');
      }
    };

    exchange();
  }, [navigate, loginWithToken]);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 border-2 border-[#e8c547] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#a0a0a0] text-sm">Completing Google sign-in…</p>
      </div>
    </div>
  );
}
